import * as Yup from 'yup';
import i18n from 'i18n';
import { ExperiencePricingTypeEnum } from '@koob/enums';
import { getCancelConditionsSchema } from 'components/Fields/CancelConditionField/utils';

export const EXPERIENCE_TYPOLOGY_SCHEMA = Yup.object().shape({
  type: Yup.string().required(i18n.t('errors:required')),
});

export const EXPERIENCE_SERVICE_SCHEMA = Yup.object().shape({
  activity: Yup.object().shape({
    isExtra: Yup.bool().required(i18n.t('errors:required')),
  }),
});

export const EXPERIENCE_COMPATIBILITY_SCHEMA = Yup.object().shape({
  extras: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required(i18n.t('errors:required')),
    }),
  ),
});

export const EXPERIENCE_OPTIONS_SCHEMA = Yup.object().shape({
  extras: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required(i18n.t('errors:required')),
    }),
  ),
});

export const EXPERIENCE_CONFIG_SCHEMA = Yup.object().shape({
  dmcReference: Yup.string().nullable(),
  name: Yup.string().required(i18n.t('errors:required')),
  durationDays: Yup.number()
    .required(i18n.t('errors:required'))
    .min(0, i18n.t('errors:minValue', { min: 0 })),
  durationHours: Yup.number()
    .required(i18n.t('errors:required'))
    .min(0, i18n.t('errors:minValue', { min: 0 }))
    .max(23, i18n.t('errors:maxValue', { max: 24 })),
  durationMinutes: Yup.number()
    .required(i18n.t('errors:required'))
    .min(0, i18n.t('errors:minValue', { min: 0 }))
    .max(59, i18n.t('errors:maxValue', { max: 60 })),
  guiding: Yup.boolean().required(i18n.t('errors:required')),
  guidingLanguages: Yup.array().when('guiding', {
    is: true,
    then: (schema) => schema.min(1).required(i18n.t('errors:required')),
    otherwise: (schema) => schema.nullable(),
  }),
  distance: Yup.number().when('activity.isExtra', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.min(0, i18n.t('errors:minValue', { min: 0 })),
  }),
  inCountry: Yup.object().when('activity.isExtra', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.required(i18n.t('errors:required')),
  }),
  inCity: Yup.object().when('activity.isExtra', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.required(i18n.t('errors:required')),
  }),
  startPosition: Yup.object().when('activity.isExtra', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.required(i18n.t('errors:required')),
  }),
  endPosition: Yup.object().when('activity.isExtra', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.required(i18n.t('errors:required')),
  }),
  locationsExtraInformations: Yup.string().nullable(),
  description: Yup.string().required(i18n.t('errors:required')),
  includedServices: Yup.string().nullable(),
  excludedServices: Yup.string().nullable(),
  extraRemarks: Yup.string().nullable(),
});

export const EXPERIENCE_ACTIVITIES_SCHEMA = Yup.object().shape({
  highlights: Yup.string().nullable(),
  difficulty: Yup.string(),
  activity: Yup.object().shape({
    pickUpPoint: Yup.string().nullable(),
    dropOffPoint: Yup.string().nullable(),
    pickUpPointTimeHour: Yup.number()
      .min(0, i18n.t('errors:minValue', { min: 0 }))
      .max(23, i18n.t('errors:maxValue', { max: 24 })),
    pickUpPointTimeMin: Yup.number()
      .min(0, i18n.t('errors:minValue', { min: 0 }))
      .max(59, i18n.t('errors:maxValue', { max: 60 })),
    dropOffPointTimeHour: Yup.number()
      .min(0, i18n.t('errors:minValue', { min: 0 }))
      .max(23, i18n.t('errors:maxValue', { max: 24 })),
    dropOffPointTimeMin: Yup.number()
      .min(0, i18n.t('errors:minValue', { min: 0 }))
      .max(59, i18n.t('errors:maxValue', { max: 60 })),
    freeVisitInformations: Yup.string().nullable(),
  }),
});

export const EXPERIENCE_PROGRAMS_DAYS_SCHEMA = Yup.object().shape({
  programs: Yup.array().of(
    Yup.object().shape({
      picture: Yup.mixed(),
      title: Yup.string().required(i18n.t('errors:required')),
      startPosition: Yup.object().required(i18n.t('errors:required')),
      endPosition: Yup.object().required(i18n.t('errors:required')),
      startingTimeHour: Yup.number()
        .min(0, i18n.t('errors:minValue', { min: 0 }))
        .max(23, i18n.t('errors:maxValue', { max: 24 })),
      startingTimeMin: Yup.number()
        .min(0, i18n.t('errors:minValue', { min: 0 }))
        .max(59, i18n.t('errors:maxValue', { max: 60 })),
      endingTimeHour: Yup.number()
        .min(0, i18n.t('errors:minValue', { min: 0 }))
        .max(23, i18n.t('errors:maxValue', { max: 24 })),
      endingTimeMin: Yup.number()
        .min(0, i18n.t('errors:minValue', { min: 0 }))
        .max(59, i18n.t('errors:maxValue', { max: 60 })),
      program: Yup.string().required(i18n.t('errors:required')),
      meals: Yup.array().of(Yup.object()),
      mealExperience: Yup.object(),
      accomodationType: Yup.string().required(i18n.t('errors:required')),
      accomodationExperience: Yup.object().when(
        'accomodationType',
        (accomodationType) => {
          if (accomodationType === 'nightTransfer') {
            return Yup.object().required(i18n.t('errors:required'));
          }
          return Yup.object();
        },
      ),
      accomodationHotel: Yup.object().when(
        'accomodationType',
        (accomodationType) => {
          if (accomodationType === 'hotel') {
            return Yup.object().required(i18n.t('errors:required'));
          }
          return Yup.object();
        },
      ),
    }),
  ),
});

export const EXPERIENCE_PROGRAM_SCHEMA = Yup.object().shape({
  highlights: Yup.string().nullable(),
  difficulty: Yup.string().nullable(),
});

export const EXPERIENCE_TRANSFER_CONFIG_SCHEMA = Yup.object().shape({
  transfer: Yup.object().shape({
    type: Yup.string().required(i18n.t('errors:required')),
    departureTimeHour: Yup.number()
      .min(0, i18n.t('errors:minValue', { min: 0 }))
      .max(23, i18n.t('errors:maxValue', { max: 24 })),
    departureTimeMin: Yup.number()
      .min(0, i18n.t('errors:minValue', { min: 0 }))
      .max(59, i18n.t('errors:maxValue', { max: 60 })),
    arrivalTimeHour: Yup.number()
      .min(0, i18n.t('errors:minValue', { min: 0 }))
      .max(23, i18n.t('errors:maxValue', { max: 24 })),
    arrivalTimeMin: Yup.number()
      .min(0, i18n.t('errors:minValue', { min: 0 }))
      .max(59, i18n.t('errors:maxValue', { max: 60 })),
    time: Yup.string().nullable(),
  }),
});

export const EXPERIENCE_TRANSFER_LAND_SCHEMA = Yup.object().shape({
  transfer: Yup.object().shape({
    land: Yup.object().shape({
      pickUpPoint: Yup.string().nullable(),
      dropOffPoint: Yup.string().nullable(),
      vehicleType: Yup.string().nullable(),
    }),
  }),
});

export const EXPERIENCE_TRANSFER_AIR_SCHEMA = Yup.object().shape({
  transfer: Yup.object().shape({
    air: Yup.object().shape({
      taxesIncluded: Yup.string().nullable(),
      taxes: Yup.number()
        .nullable()
        .min(0, i18n.t('errors:minValue', { min: 0 })),
      flightNumber: Yup.string().nullable(),
      airplaneModel: Yup.string().nullable(),
      airlineCompany: Yup.string().nullable(),
      onboardServices: Yup.string().nullable(),
      seatClass: Yup.string().nullable(),
      luggagePolicies: Yup.string().nullable(),
    }),
  }),
});

export const EXPERIENCE_TRANSFER_RAIL_SCHEMA = Yup.object().shape({
  transfer: Yup.object().shape({
    rail: Yup.object().shape({
      departureStation: Yup.string().required(i18n.t('errors:required')),
      arrivalStation: Yup.string().required(i18n.t('errors:required')),
      trainName: Yup.string().nullable(),
      trainNumber: Yup.string().nullable(),
      privateCabin: Yup.string().nullable(),
      seats: Yup.number()
        .min(0, i18n.t('errors:minValue', { min: 0 }))
        .nullable(),
      class: Yup.string().nullable(),
      luggagePolicies: Yup.string().nullable(),
    }),
  }),
});

export const EXPERIENCE_TRANSFER_BOAT_SCHEMA = Yup.object().shape({
  transfer: Yup.object().shape({
    boat: Yup.object().shape({
      departureLocation: Yup.string().required(i18n.t('errors:required')),
      arrivalLocation: Yup.string().required(i18n.t('errors:required')),
      boatName: Yup.string().nullable(),
      boatNumber: Yup.string().nullable(),
      seats: Yup.number()
        .min(0, i18n.t('errors:minValue', { min: 0 }))
        .nullable(),
      cabins: Yup.number()
        .min(0, i18n.t('errors:minValue', { min: 0 }))
        .nullable(),
      class: Yup.string().nullable(),
      luggagePolicies: Yup.string().nullable(),
    }),
  }),
});

export const EXPERIENCE_MEDIA_SCHEMA = Yup.object().shape({
  mediaLinks: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required(i18n.t('errors:required')),
      link: Yup.string().required(i18n.t('errors:required')),
    }),
  ),
});

export const EXPERIENCE_PRICING_SCHEMA = Yup.object().shape({
  currency: Yup.string().required(i18n.t('errors:required')),
  periods: Yup.array()
    .required(i18n.t('errors:required'))
    .min(1, i18n.t('errors:minItems', { min: 1 }))
    .of(
      Yup.object().shape({
        state: Yup.string().when('$pricingType', {
          is: ExperiencePricingTypeEnum.FIT,
          then: Yup.string().required(i18n.t('errors:required')),
          otherwise: (schema) => schema.nullable(),
        }),
        startAt: Yup.date().required(i18n.t('errors:required')),
        endAt: Yup.date()
          .required(i18n.t('errors:required'))
          .test(
            'end-date-is-valid',
            i18n.t('errors:startDateGreaterThanEndDate'),
            function (value) {
              const { startAt } = this.parent;
              return !startAt || !value || value >= startAt;
            },
          ),
        days: Yup.object().when('$pricingType', {
          is: ExperiencePricingTypeEnum.FIT,
          then: (schema) =>
            schema
              .required(i18n.t('errors:required'))
              .test('days-is-valid', i18n.t('errors:required'), (value) => {
                return (
                  Object.keys(value).filter((key) => value[key] === true)
                    .length > 0
                );
              }),
          otherwise: (schema) => schema.nullable(),
        }),
        price: Yup.number()
          // Empty input must not trigger the number type error when price is optional
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value,
          )
          .when('perPax', {
            // Price comes from the adult/children pax grids when per pax pricing is on
            is: (perPax) => perPax === true || perPax === 'true',
            then: (schema) =>
              schema.test(
                'must-be-empty',
                i18n.t('errors:mustBeEmpty'),
                (value) => value === undefined,
              ),
            otherwise: (schema) => schema.required(i18n.t('errors:required')),
          })
          .when('$activity', {
            is: (activity) => activity && activity.isExtra === true,
            then: (schema) => schema,
            otherwise: (schema) =>
              schema.min(0, i18n.t('errors:minValue', { min: 0 })),
          }),
        perPax: Yup.string().required(i18n.t('errors:required')),
        minPax: Yup.number()
          .required(i18n.t('errors:required'))
          .min(1, i18n.t('errors:minValue', { min: 0 }))
          .test(
            'min-pax-matches-adult-ranges',
            i18n.t('errors:minPaxMustMatchAdultPaxRanges'),
            function (value) {
              const { perPax, adultPax } = this.parent;
              if (perPax !== true && perPax !== 'true') return true;
              if (!Number.isFinite(value)) return true;
              const lowerBounds = (adultPax ?? [])
                .map((range) => range?.paxRange?.[0])
                .filter((bound) => Number.isFinite(bound));
              if (lowerBounds.length === 0) return true;
              return value === Math.min(...lowerBounds);
            },
          ),
        maxPax: Yup.number()
          .required(i18n.t('errors:required'))
          .min(1, i18n.t('errors:minValue', { min: 0 }))
          .test(
            'max-pax-matches-adult-ranges',
            i18n.t('errors:maxPaxMustMatchAdultPaxRanges'),
            function (value) {
              const { perPax, adultPax } = this.parent;
              if (perPax !== true && perPax !== 'true') return true;
              if (!Number.isFinite(value)) return true;
              const upperBounds = (adultPax ?? [])
                .map((range) => range?.paxRange?.[1])
                .filter((bound) => Number.isFinite(bound));
              if (upperBounds.length === 0) return true;
              return value === Math.max(...upperBounds);
            },
          ),
        adultPax: Yup.array()
          .of(
            Yup.object().shape({
              price: Yup.number()
                .required(i18n.t('errors:required'))
                .min(0, i18n.t('errors:minValue', { min: 0 })),
              paxRange: Yup.array().test(
                'pax-range-is-valid',
                i18n.t('errors:rangeNotValid'),
                (value) => {
                  return (
                    value &&
                    value.length === 2 &&
                    value[0] <= value[1] &&
                    value[0] > 0
                  );
                },
              ),
            }),
          )
          .nullable()
          .when('perPax', {
            // Per pax periods are priced from this grid, so it cannot be left
            // empty nor omitted entirely
            is: (perPax) => perPax === true || perPax === 'true',
            then: (schema) =>
              schema
                .required(i18n.t('errors:required'))
                .min(1, i18n.t('errors:minItems', { min: 1 })),
          }),
        childrenPax: Yup.array()
          .of(
            Yup.object().shape({
              price: Yup.number()
                .required(i18n.t('errors:required'))
                .min(0, i18n.t('errors:minValue', { min: 0 })),
              ageRange: Yup.array().test(
                'age-range-is-valid',
                i18n.t('errors:rangeNotValid'),
                (value) => {
                  return (
                    value &&
                    value.length === 2 &&
                    value[0] < value[1] &&
                    value[0] >= 0
                  );
                },
              ),
            }),
          )
          .nullable(),
      }),
    ),
  isReservableAlone: Yup.boolean().required(i18n.t('errors:required')),
  isRepeatable: Yup.boolean().required(i18n.t('errors:required')),
});

export const EXPERIENCE_ACCOMMODATION_SCHEMA = Yup.object().shape({
  accommodationCategories: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required(i18n.t('errors:required')),
      }),
    )
    .nullable(),
  accommodationRooms: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required(i18n.t('errors:required')),
        paxCount: Yup.number()
          .required(i18n.t('errors:required'))
          .min(1, i18n.t('errors:minValue', { min: 1 })),
        adultsCount: Yup.number()
          .required(i18n.t('errors:required'))
          .min(1, i18n.t('errors:minValue', { min: 1 })),
        childrenCount: Yup.number()
          .required(i18n.t('errors:required'))
          .min(0, i18n.t('errors:minValue', { min: 0 })),
      }),
    )
    .nullable(),
});

export const EXPERIENCE_CONDITION_SCHEMA = Yup.object().shape({
  cancellationPolicies: Yup.string()
    .nullable()
    .required(i18n.t('errors:required')),

  cancelConditions: getCancelConditionsSchema(false),
});

export const EXPERIENCE_TRANSFERS_SCHEMA = Yup.object().shape({});
